import React, { useRef, useCallback } from 'react';
import { _cs } from '@togglecorp/fujs';
import { IoClose, IoChevronUp, IoChevronDown } from '../../node_modules/react-icons/io5/index.esm.js';
import GenericSelectOption from '../GenericSelectOption/index.js';
import Popup from '../Popup/index.js';
import InputContainer from '../InputContainer/index.js';
import RawInput from '../RawInput/index.js';
import Button from '../Button/index.js';
import List from '../List.js';
import useBlurEffect from '../../hooks/useBlurEffect.js';
import useKeyboard from '../../hooks/useKeyboard.js';
import { genericMemo } from '../../utils/index.js';
import EmptyOptions from './EmptyOptions/index.js';
import styles from './styles.css.js';

var _IoClose;

function Group(_ref) {
  let {
    className,
    title,
    children,
    headerContainerClassName,
    childrenContainerClassName
  } = _ref;
  return /*#__PURE__*/React.createElement("div", {
    className: _cs(className, styles.group)
  }, /*#__PURE__*/React.createElement("header", {
    className: _cs(headerContainerClassName, styles.groupHeader),
    title: title
  }, title), /*#__PURE__*/React.createElement("div", {
    className: _cs(childrenContainerClassName, styles.groupChildren)
  }, children));
}

const emptyList = []; // eslint-disable-next-line @typescript-eslint/ban-types, max-len

function SelectInputContainer(props) {
  const {
    actions,
    actionsContainerClassName,
    className,
    disabled,
    error,
    errorContainerClassName,
    hint,
    hintContainerClassName,
    icons,
    iconsContainerClassName,
    inputSectionClassName,
    label,
    labelContainerClassName,
    name,
    onOptionClick,
    searchText,
    onSearchTextChange,
    optionContainerClassName,
    optionKeySelector,
    optionRenderer,
    optionRendererParams,
    options: optionsFromProps,
    optionsPopupClassName,
    optionsPopupContentClassName,
    persistentOptionPopup,
    readOnly,
    placeholder,
    uiMode,
    valueDisplay = '',
    nonClearable,
    onClear,
    optionsPending,
    optionsFiltered,
    focused,
    focusedKey,
    onFocusedKeyChange,
    onFocusedChange,
    onDropdownShownChange,
    totalOptionsCount,
    handleShowMoreClick,
    variant,
    inputDescription,
    hasValue,
    inputClassName,
    autoFocus
  } = props;
  const options = optionsFromProps ?? emptyList;
  const inputSectionRef = useRef(null);
  const inputElementRef = useRef(null);
  const popupRef = useRef(null);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const handleSearchInputChange = useCallback(value => {
    if (!showDropdown) {
      onDropdownShownChange(true);
    }

    onSearchTextChange(value);
  }, [showDropdown, onDropdownShownChange, onSearchTextChange]);
  const handleShowDropdown = useCallback(() => {
    setShowDropdown(prevShown => {
      if (!prevShown) {
        onDropdownShownChange(true);
      }

      return true;
    });
  }, [onDropdownShownChange]);
  const handleHideDropdown = useCallback(() => {
    setShowDropdown(false);
  }, []);
  const handleSearchInputClick = useCallback(() => {
    if (readOnly) {
      return;
    }

    handleShowDropdown();
  }, [readOnly, handleShowDropdown]);
  const handlePopupBlur = useCallback((isClickedOnPopup, isClickedOnParent) => {
    const isClickedWithin = isClickedOnPopup || isClickedOnParent;

    if (!isClickedWithin) {
      handleHideDropdown();
    } else if (persistentOptionPopup && inputElementRef.current) {
      inputElementRef.current.focus();
    }
  }, [handleHideDropdown, persistentOptionPopup]);
  const handleOptionClick = useCallback((valueKey, value) => {
    onOptionClick(valueKey, value, name);

    if (!persistentOptionPopup) {
      handleHideDropdown();
    }
  }, [onOptionClick, handleHideDropdown, persistentOptionPopup, name]);
  const handlePopupUnmount = useCallback(() => {
    onDropdownShownChange(false);
  }, [onDropdownShownChange]);
  const optionListRendererParams = useCallback((key, option) => ({
    contentRendererParam: optionRendererParams,
    option,
    optionKey: key,
    focusedKey,
    contentRenderer: optionRenderer,
    onClick: handleOptionClick,
    onFocus: onFocusedKeyChange,
    optionContainerClassName: _cs(optionContainerClassName, styles.listItem)
  }), [focusedKey, handleOptionClick, onFocusedKeyChange, optionContainerClassName, optionRenderer, optionRendererParams]);
  const groupRendererParams = useCallback((_, __, values) => ({
    // eslint-disable-next-line react/destructuring-assignment
    title: props.grouped ? props.groupLabelSelector(values[0]) : '?'
  }), // FIXME: disabling because linter is not smart enough
  // eslint-disable-next-line react-hooks/exhaustive-deps, react/destructuring-assignment
  [props.grouped, props.groupLabelSelector]);
  useBlurEffect(showDropdown, handlePopupBlur, popupRef, inputSectionRef);
  const handleKeyDown = useKeyboard(focusedKey, optionKeySelector, options, showDropdown, onFocusedKeyChange, handleHideDropdown, handleShowDropdown, handleOptionClick);
  let popupContent; // eslint-disable-next-line react/destructuring-assignment

  if (props.grouped) {
    popupContent = /*#__PURE__*/React.createElement(List, {
      data: options,
      keySelector: optionKeySelector,
      renderer: GenericSelectOption,
      rendererParams: optionListRendererParams,
      grouped: true,
      groupRenderer: Group,
      groupRendererParams: groupRendererParams // eslint-disable-next-line react/destructuring-assignment
      ,
      groupKeySelector: props.groupKeySelector // eslint-disable-next-line react/destructuring-assignment
      ,
      groupComparator: props.groupComparator
    });
  } else {
    popupContent = /*#__PURE__*/React.createElement(List, {
      data: options,
      keySelector: optionKeySelector,
      renderer: GenericSelectOption,
      rendererParams: optionListRendererParams
    });
  }

  return /*#__PURE__*/React.createElement(InputContainer, {
    inputSectionRef: inputSectionRef,
    actions: /*#__PURE__*/React.createElement(React.Fragment, null, actions, !readOnly && !nonClearable && hasValue && /*#__PURE__*/React.createElement(Button, {
      onClick: onClear,
      disabled: disabled,
      variant: "action",
      name: undefined,
      title: "Clear"
    }, _IoClose || (_IoClose = /*#__PURE__*/React.createElement(IoClose, null))), !readOnly && (showDropdown ? /*#__PURE__*/React.createElement(IoChevronUp, {
      onClick: handleHideDropdown
    }) : /*#__PURE__*/React.createElement(IoChevronDown, {
      onClick: handleSearchInputClick
    }))),
    actionsContainerClassName: actionsContainerClassName,
    className: className,
    disabled: disabled,
    error: error,
    errorContainerClassName: errorContainerClassName,
    hint: hint,
    hintContainerClassName: hintContainerClassName,
    icons: icons,
    iconsContainerClassName: iconsContainerClassName,
    inputSectionClassName: inputSectionClassName,
    label: label,
    labelContainerClassName: labelContainerClassName,
    readOnly: readOnly,
    uiMode: uiMode,
    variant: variant,
    input: /*#__PURE__*/React.createElement(RawInput, {
      className: inputClassName,
      name: name,
      elementRef: inputElementRef,
      readOnly: readOnly,
      uiMode: uiMode,
      disabled: disabled,
      value: showDropdown || focused ? searchText : valueDisplay,
      onChange: handleSearchInputChange,
      onClick: handleSearchInputClick // FIXME: memoize this
      ,
      onFocus: () => onFocusedChange(true),
      onBlur: () => onFocusedChange(false),
      placeholder: valueDisplay || placeholder,
      autoComplete: "off",
      onKeyDown: handleKeyDown,
      autoFocus: autoFocus
    }),
    inputDescription: inputDescription
  }, /*#__PURE__*/React.createElement(Popup, {
    parentRef: inputSectionRef,
    elementRef: popupRef,
    className: _cs(optionsPopupClassName, styles.popup),
    contentClassName: _cs(styles.popupContent, // eslint-disable-next-line react/destructuring-assignment
    props.grouped && styles.grouped, optionsPopupContentClassName),
    show: showDropdown,
    onUnmount: handlePopupUnmount
  }, popupContent, /*#__PURE__*/React.createElement(EmptyOptions, {
    filtered: optionsFiltered,
    pending: optionsPending,
    optionsCount: options.length,
    totalOptionsCount: totalOptionsCount,
    handleShowMoreClick: handleShowMoreClick
  })));
}

var SelectInputContainer$1 = genericMemo(SelectInputContainer);

export { SelectInputContainer$1 as default };
//# sourceMappingURL=index.js.map
